import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import {
  springPresets,
  fadeInUp,
  staggerContainer,
  staggerItem,
} from "@/lib/motion";
import {
  PRACTICE_AREAS,
  CIVIL_PROCESS_STEPS,
  CRIMINAL_PROCESS_STEPS,
  FAQS,
  TESTIMONIALS,
  PHONE,
  ADDRESS,
  HOURS,
  WHATSAPP_LINK,
  FIRM_NAME,
} from "@/data";
import {
  ChevronDown,
  Star,
  Phone,
  MapPin,
  Clock,
  MessageCircle,
} from "lucide-react";

// ── Hero ─────────────────────────────────────────────────────────────────────
export function HeroSection() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center bg-primary overflow-hidden"
    >
      {/* Background pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, currentColor 0, currentColor 1px, transparent 0, transparent 50%)",
          backgroundSize: "20px 20px",
          color: "white",
        }}
      />

      <div className="container mx-auto px-4 pt-24 pb-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text content */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-primary-foreground"
          >
            <motion.div variants={staggerItem}>
              <span className="inline-block bg-accent/20 text-accent border border-accent/30 text-xs font-semibold px-3 py-1 rounded-full mb-6 tracking-wide uppercase">
                Singapore Civil & Criminal Trial Lawyers
              </span>
            </motion.div>

            <motion.h1
              variants={staggerItem}
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Civil & Criminal
              <span className="block text-accent">Trial Lawyers</span>
            </motion.h1>

            <motion.p
              variants={staggerItem}
              className="text-primary-foreground/80 text-lg md:text-xl leading-relaxed mb-8 max-w-xl"
            >
              Experienced civil litigation and criminal defence lawyers
              in Singapore. From contract disputes and debt recovery
              to criminal trial representation — we act with precision,
              strategy, and tenacity.
            </motion.p>

            {/* Dual category badges */}
            <motion.div variants={staggerItem} className="flex flex-wrap gap-3 mb-8">
              <span className="bg-blue-500/20 text-blue-300 border border-blue-400/30 text-xs font-semibold px-3 py-1.5 rounded-full">
                ⚖️ Civil Litigation
              </span>
              <span className="bg-red-500/20 text-red-300 border border-red-400/30 text-xs font-semibold px-3 py-1.5 rounded-full">
                🔒 Criminal Defence
              </span>
            </motion.div>

            {/* CTA buttons */}
            <motion.div variants={staggerItem} className="flex flex-wrap gap-4 mb-10">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-accent text-sm font-bold px-6 py-3 rounded-md hover:bg-accent/90 transition-all duration-200 hover:scale-105"
                style={{ color: "#0a1628" }}
              >
                💬 Free WhatsApp Consultation
              </a>
              <a
                href={`tel:${PHONE.replace(/\s/g, "")}`}
                className="border border-primary-foreground/30 text-primary-foreground text-sm font-semibold px-6 py-3 rounded-md hover:bg-primary-foreground/10 transition-all duration-200 flex items-center gap-2"
              >
                <Phone size={16} />
                {PHONE}
              </a>
            </motion.div>

          </motion.div>

          {/* Hero image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.3 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-accent/10 rounded-2xl" />
              <img
                src="/images/hero_legal_abstract_20260516_143830.jpeg"
                alt="Trial Defence Law — Civil and Criminal Trial Lawyers Singapore"
                className="relative rounded-xl shadow-2xl w-full object-cover max-h-[520px]"
                loading="eager"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
export function TrustSection() {
  const points = [
    {
      icon: "🏛️",
      title: "Court-Ready Representation",
      desc: "Our lawyers appear before Singapore's Magistrate's Courts, District Courts, and the High Court on a near-daily basis, handling both civil and criminal matters. Your case will be handled by experienced trial lawyers.",
    },
    {
      icon: "🔍",
      title: "Honest Case Assessment",
      desc: "We provide a candid, evidence-based assessment of your case from the outset — so you can make informed decisions on your civil dispute or criminal matter with clarity and confidence.",
    },
    {
      icon: "⚡",
      title: "Swift, Decisive Action",
      desc: "Whether filing urgent applications, seeking urgent relief, applying for bail, or responding to critical developments — we act quickly and strategically. In legal matters, delay can carry serious consequences.",
    },
    {
      icon: "💼",
      title: "Transparent Fees",
      desc: "We believe legal fees should be discussed clearly from the outset. We provide upfront fee estimates, practical guidance on anticipated costs, and complete transparency — with no hidden charges or surprise bills — so you can proceed with confidence.",
    },
  ];

  return (
    <section className="py-20 bg-background" id="why-us">
      <div className="container mx-auto px-4">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <h2
            className="text-3xl md:text-4xl font-bold text-foreground mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Why Choose Our Civil &amp; Criminal Trial Lawyers
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            For both civil disputes and criminal defence in Singapore, you need
            lawyers who are experienced, honest, and committed to your outcome.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {points.map((p) => (
            <motion.div
              key={p.title}
              variants={staggerItem}
              className="bg-card rounded-xl p-6 border border-border hover:border-accent/40 transition-all duration-300 hover:shadow-lg group"
            >
              <div className="text-3xl mb-4">{p.icon}</div>
              <h3
                className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {p.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {p.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── Services ──────────────────────────────────────────────────────────────────
export function ServicesSection() {
  const [activeTab, setActiveTab] = useState<"all" | "civil" | "criminal">("all");

  const filtered =
    activeTab === "all"
      ? PRACTICE_AREAS
      : PRACTICE_AREAS.filter((a) => a.category === activeTab);

  return (
    <section id="services" className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2
            className="text-3xl md:text-4xl font-bold text-foreground mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Civil &amp; Criminal Trial Lawyers in Singapore
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Our lawyers represent clients in civil litigation and criminal defence matters across all levels of Singapore's courts — from the Magistrate's Courts and District Courts to the High Court.
          </p>

          {/* Filter tabs */}
          <div className="inline-flex bg-card border border-border rounded-lg p-1 gap-1">
            {(["all", "civil", "criminal"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 capitalize ${
                  activeTab === tab
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab === "all" ? "All Services" : tab === "civil" ? "⚖️ Civil" : "🔒 Criminal"}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          key={activeTab}
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filtered.map((area) => (
            <motion.article
              key={area.id}
              variants={staggerItem}
              className={`bg-card rounded-xl p-7 border-t-2 hover:shadow-xl transition-all duration-300 group ${
                area.category === "criminal"
                  ? "border-red-400"
                  : "border-accent"
              }`}
            >
              <div className="text-4xl mb-4">{area.icon}</div>
              <div className="mb-2">
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    area.category === "criminal"
                      ? "bg-red-100 text-red-700"
                      : "bg-blue-100 text-blue-700"
                  }`}
                >
                  {area.category === "criminal" ? "Criminal" : "Civil"}
                </span>
              </div>
              <h3
                className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {area.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                {area.desc}
              </p>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm font-semibold text-primary hover:text-accent transition-colors"
              >
                Enquire Now →
              </a>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
export function ProcessSection() {
  const [activeProcess, setActiveProcess] = useState<"civil" | "criminal">("civil");

  const steps =
    activeProcess === "civil" ? CIVIL_PROCESS_STEPS : CRIMINAL_PROCESS_STEPS;

  return (
    <section id="process" className="py-20 bg-primary">
      <div className="container mx-auto px-4">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2
            className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            How Claim Trials Work in Singapore
          </h2>
          <p className="text-primary-foreground/70 max-w-2xl mx-auto mb-8">
            The civil litigation and criminal defence processes follow distinct
            procedures in Singapore. Select the relevant process below.
          </p>

          {/* Toggle */}
          <div className="inline-flex bg-primary-foreground/10 border border-primary-foreground/20 rounded-lg p-1 gap-1">
            <button
              onClick={() => setActiveProcess("civil")}
              className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 ${
                activeProcess === "civil"
                  ? "bg-accent text-primary-foreground shadow"
                  : "text-primary-foreground/70 hover:text-primary-foreground"
              }`}
              style={activeProcess === "civil" ? { color: "#0a1628" } : {}}
            >
              ⚖️ Civil Litigation
            </button>
            <button
              onClick={() => setActiveProcess("criminal")}
              className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 ${
                activeProcess === "criminal"
                  ? "bg-red-500 text-white shadow"
                  : "text-primary-foreground/70 hover:text-primary-foreground"
              }`}
            >
              🔒 Criminal Defence
            </button>
          </div>
        </motion.div>

        {/* Process context banner */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeProcess}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className={`mb-8 rounded-xl p-4 border text-sm text-center max-w-3xl mx-auto ${
              activeProcess === "civil"
                ? "bg-blue-500/10 border-blue-400/20 text-blue-200"
                : "bg-red-500/10 border-red-400/20 text-red-200"
            }`}
          >
            {activeProcess === "civil"
              ? "⚖️ Civil proceedings are governed by the Rules of Court 2021. The claimant bears the burden of proof on a balance of probabilities."
              : "🔒 Criminal proceedings are governed by the Criminal Procedure Code. The prosecution must prove the case beyond a reasonable doubt — the accused is presumed innocent."}
          </motion.div>
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeProcess + "-steps"}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 relative"
          >
            {steps.map((step, idx) => (
              <div
                key={step.step}
                className="relative bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl p-6 hover:bg-primary-foreground/10 transition-all duration-300"
              >
                <div
                  className={`text-5xl font-bold mb-3 ${
                    activeProcess === "civil"
                      ? "text-accent/30"
                      : "text-red-400/30"
                  }`}
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {step.step}
                </div>
                <h3
                  className="text-lg font-bold text-primary-foreground mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {step.title}
                </h3>
                <p className="text-primary-foreground/60 text-sm leading-relaxed">
                  {step.desc}
                </p>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-accent text-sm font-bold px-8 py-4 rounded-md hover:bg-accent/90 transition-all duration-200 hover:scale-105"
            style={{ color: "#0a1628" }}
          >
            Start Your Free Consultation Today
          </a>
        </motion.div>
      </div>
    </section>
  );
}

// ── Testimonials ──────────────────────────────────────────────────────────────
export function TestimonialsSection() {
  return (
    <section className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <h2
            className="text-3xl md:text-4xl font-bold text-foreground mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            What Our Clients Say
          </h2>
          <p className="text-muted-foreground">
            Real results for real people — civil and criminal matters across Singapore
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {TESTIMONIALS.map((t, idx) => (
            <motion.div
              key={idx}
              variants={staggerItem}
              className="bg-card rounded-xl p-7 border border-border hover:border-accent/30 transition-all duration-300 hover:shadow-lg"
              itemScope
              itemType="https://schema.org/Review"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(t.stars)].map((_, i) => (
                  <Star key={i} size={16} fill="#c9a84c" stroke="none" />
                ))}
              </div>
              <blockquote
                className="text-muted-foreground text-sm leading-relaxed mb-5 italic"
                itemProp="reviewBody"
              >
                "{t.text}"
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">
                  {t.name[0]}
                </div>
                <div>
                  <div
                    className="font-semibold text-foreground text-sm"
                    itemProp="author"
                  >
                    {t.name}
                  </div>
                  <div className="text-muted-foreground text-xs">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── FAQ ────────────────────────────────────────────────────────────────────────
export function FAQSection() {
  const [open, setOpen] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "civil" | "criminal">("all");

  const filtered =
    activeTab === "all"
      ? FAQS
      : FAQS.filter((f) => f.category === activeTab);

  return (
    <section id="faq" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2
            className="text-3xl md:text-4xl font-bold text-foreground mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            Common questions about civil litigation and criminal defence in Singapore
          </p>

          <div className="inline-flex bg-secondary/50 border border-border rounded-lg p-1 gap-1">
            {(["all", "civil", "criminal"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); setOpen(null); }}
                className={`px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 capitalize ${
                  activeTab === tab
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab === "all" ? "All" : tab === "civil" ? "⚖️ Civil" : "🔒 Criminal"}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-3">
          {filtered.map((faq, idx) => (
            <motion.div
              key={`${activeTab}-${idx}`}
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="border border-border rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpen(open === idx ? null : idx)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-secondary/50 transition-colors"
                aria-expanded={open === idx}
              >
                <h3 className="font-semibold text-foreground text-sm md:text-base pr-4">
                  {faq.q}
                </h3>
                <ChevronDown
                  size={18}
                  className={`shrink-0 text-accent transition-transform duration-200 ${
                    open === idx ? "rotate-180" : ""
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {open === idx && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <p className="px-5 pb-5 text-muted-foreground text-sm leading-relaxed border-t border-border pt-4">
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact ────────────────────────────────────────────────────────────────────
export function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    matter: "civil",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = encodeURIComponent(
      `Hello,\n\nMy name is ${form.name}.\nPhone: ${form.phone}\nMatter Type: ${form.matter}\n\nDescription: ${form.message}\n\nPlease advise.`
    );
    window.open(`https://wa.me/6580148989?text=${msg}`, "_blank");
    setSent(true);
  };

  return (
    <section id="contact" className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2
              className="text-3xl md:text-4xl font-bold text-foreground mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Get Your Free Consultation
            </h2>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Contact us for a free, confidential consultation about your civil
              dispute or criminal matter. We advise on both civil litigation
              and criminal defence across Singapore's courts.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4 bg-card p-4 rounded-xl border border-border">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <MapPin size={18} className="text-primary" />
                </div>
                <div>
                  <div className="font-semibold text-foreground text-sm mb-1">
                    Office Address
                  </div>
                  <div className="text-muted-foreground text-sm">{ADDRESS}</div>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-card p-4 rounded-xl border border-border">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Phone size={18} className="text-primary" />
                </div>
                <div>
                  <div className="font-semibold text-foreground text-sm mb-1">
                    Call / WhatsApp
                  </div>
                  <a
                    href={`tel:${PHONE.replace(/\s/g, "")}`}
                    className="text-muted-foreground text-sm hover:text-foreground transition-colors"
                  >
                    {PHONE}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-card p-4 rounded-xl border border-border">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Clock size={18} className="text-primary" />
                </div>
                <div>
                  <div className="font-semibold text-foreground text-sm mb-1">
                    Office Hours
                  </div>
                  <div className="text-muted-foreground text-sm">{HOURS}</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            {sent ? (
              <div className="bg-card rounded-xl p-10 border border-border text-center">
                <div className="text-5xl mb-4">✅</div>
                <h3
                  className="text-xl font-bold text-foreground mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Message Sent!
                </h3>
                <p className="text-muted-foreground text-sm">
                  Your WhatsApp message has been prepared. We will respond
                  within 1 business hour.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-6 text-sm text-primary hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-card rounded-xl p-8 border border-border shadow-sm"
              >
                <h3
                  className="text-xl font-bold text-foreground mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Free Case Evaluation
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Full Name *
                    </label>
                    <input
                      required
                      type="text"
                      className="w-full border border-border rounded-md px-4 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Phone / WhatsApp *
                    </label>
                    <input
                      required
                      type="tel"
                      className="w-full border border-border rounded-md px-4 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                      placeholder="+65 XXXX XXXX"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Matter Type *
                    </label>
                    <select
                      className="w-full border border-border rounded-md px-4 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                      value={form.matter}
                      onChange={(e) => setForm({ ...form, matter: e.target.value })}
                    >
                      <option value="civil">⚖️ Civil Claim / Dispute</option>
                      <option value="criminal">🔒 Criminal Defence</option>
                      <option value="both">Both Civil & Criminal</option>
                      <option value="unsure">Not Sure — Need Advice</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Brief Description *
                    </label>
                    <textarea
                      required
                      rows={4}
                      className="w-full border border-border rounded-md px-4 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition resize-none"
                      placeholder="Describe your matter briefly..."
                      value={form.message}
                      onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                      }
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-primary text-primary-foreground py-3 rounded-md font-semibold text-sm hover:bg-primary/90 transition-all duration-200 hover:scale-[1.01] flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={16} />
                    Discuss Your Matter
                  </button>
                  <p className="text-xs text-muted-foreground text-center">
                    All enquiries are strictly confidential.
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
